import { LoadingContent, Spinner, Checking } from '../components/feedback';
import { useState } from 'react';
import type { UpdateStatus } from '@mcpanel/core';
import { updatesApi } from '../api/updates';
import { useApp, errorMessage } from '../app-context';
import { usePoll } from '../hooks/usePoll';
import { ErrorAlert } from '../components/ui';
import { hasUpdate } from '../components/UpdateBanner';
import { formatDateTime } from '../format';
import { ReleaseHistory } from './ReleaseHistory';
import { LauncherDownloadLink } from './LauncherDownloadLink';
export function AboutPanel() {
  const { me, canOperate, ensureSteppedUp, notify } = useApp();
  const version = usePoll(updatesApi.version, 30_000);
  const status = usePoll(updatesApi.status, 5000);
  const [pending, setPending] = useState<UpdateStatus>();
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkingLauncher, setCheckingLauncher] = useState(false);
  const perform = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };
  const data = version.data;
  const displayed = pending && (!status.data || pending.updatedAt > status.data.updatedAt) ? pending : status.data;
  const running = displayed?.state === 'pending' || displayed?.state === 'running';
  return (
    <section className="card">
      <div className="card-header">
        <h2 data-search-id="about">このパネルについて</h2>
      </div>
      <LoadingContent loading={!version.data && version.loading} shape="settings">
        <div className="card-body stack">
          <ErrorAlert error={version.error ?? status.error} />
          <p>
            版: {data?.version ?? <Checking />} ・ コミット: <code>{data?.commit ?? '不明'}</code>
          </p>
          <p>
            配布中の agent の版: <code>{data?.agentBundleVersion ?? '不明'}</code>
          </p>
          <p>
            最新の版: {data?.update?.latest ?? '未確認'}
            {data?.update?.error && ` ・ ${data.update.error}`}
            {data?.update?.url && (
              <>
                {' '}
                ・{' '}
                <a href={data.update.url} target="_blank" rel="noreferrer">
                  リリースノート
                </a>
              </>
            )}
          </p>
          {data?.update && <p className="small muted">最終確認: {formatDateTime(data.update.checkedAt)}</p>}
          <h3 data-search-id="launcher-download">ランチャー</h3>
          <LauncherDownloadLink />
          <p className="small muted">
            ランチャー: {data?.launcher?.version ?? '未確認'} ・ 最終確認:{' '}
            {data?.launcher?.checkedAt ? formatDateTime(data.launcher.checkedAt) : '未確認'}
            {data?.launcher?.error && ` ・ ${data.launcher.error}`}
          </p>
          {me?.user?.role === 'owner' && (
            <button
              className="btn"
              disabled={busy}
              onClick={() =>
                void perform(async () => {
                  setCheckingLauncher(true);
                  try {
                    await updatesApi.launcherSync();
                    await version.reload();
                  } finally {
                    setCheckingLauncher(false);
                  }
                })
              }
            >
              {busy && <Spinner />}
              {checkingLauncher ? <Checking /> : 'ランチャーを今すぐ取り込む'}
            </button>
          )}
          {canOperate && (
            <div className="row">
              <button
                className="btn"
                disabled={busy}
                onClick={() =>
                  void perform(async () => {
                    setChecking(true);
                    try {
                      await updatesApi.check();
                      await version.reload();
                    } finally {
                      setChecking(false);
                    }
                  })
                }
              >
                {busy && <Spinner />}
                {checking ? <Checking /> : '今すぐ確認'}
              </button>
              {me?.user?.role === 'owner' && (
                <button
                  className="btn primary"
                  disabled={busy || running || !hasUpdate(data)}
                  onClick={() =>
                    void perform(async () => {
                      await ensureSteppedUp();
                      setPending(await updatesApi.update(data!.update!.latest!));
                      await status.reload();
                    })
                  }
                >
                  {busy && <Spinner />}
                  更新する
                </button>
              )}
            </div>
          )}
          {data?.autoDeploy && (
            <label className="check" data-search-id="auto-update">
              <input
                type="checkbox"
                checked={data.autoDeploy.enabled}
                disabled={busy || me?.user?.role !== 'owner'}
                onChange={(event) => {
                  const enabled = event.target.checked;
                  void perform(async () => {
                    await ensureSteppedUp();
                    await updatesApi.autoDeploy(enabled);
                    await version.reload();
                  });
                }}
              />
              新しいリリースを自動で入れる（動いているジョブがないときに入れ、失敗したら前の版に戻します）
            </label>
          )}
          {displayed && (
            <div
              className={`alert ${['failed', 'rolled_back'].includes(displayed.state) ? 'warn' : 'info'}`}
              role="status"
            >
              {displayed.version}: {displayed.message}
            </div>
          )}
          <h3 data-search-id="agent-update">agent の版</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>サーバー</th>
                  <th>版</th>
                  <th>状態</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data?.agents.map((agent) => (
                  <tr key={agent.serverId}>
                    <td>{agent.serverId}</td>
                    <td>{agent.version ?? '未接続'}</td>
                    <td>
                      {agent.autoUpdate
                        ? {
                            latest: '最新',
                            updating: '更新中',
                            waiting_job: '待ち（ジョブ中）',
                            waiting_retry: '待ち（再試行）',
                            failed: '自動更新に失敗しました（手動で更新してください）',
                            disabled: '自動更新は無効（手動で更新してください）',
                            offline: '待ち（未接続）',
                          }[agent.autoUpdate]
                        : agent.outdated
                          ? '更新が必要'
                          : '最新'}
                    </td>
                    <td>
                      {canOperate && (
                        <button
                          className="btn small"
                          disabled={busy || agent.autoUpdate === 'updating'}
                          onClick={() =>
                            void perform(async () => {
                              await updatesApi.agent(agent.serverId);
                              notify('agent の更新を依頼しました');
                              await version.reload();
                            })
                          }
                        >
                          {busy && <Spinner />}
                          agent を更新
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canOperate && (
            <button
              className="btn"
              disabled={busy}
              onClick={() =>
                void perform(async () => {
                  const results = await updatesApi.agents();
                  const failed = results.filter((result) => !result.ok);
                  notify(
                    failed.length
                      ? `更新できなかった agent: ${failed.map((result) => result.serverId).join(', ')}`
                      : 'すべての agent の更新を依頼しました',
                    failed.length > 0,
                  );
                  await version.reload();
                })
              }
            >
              {busy && <Spinner />}
              すべての agent を更新
            </button>
          )}
          <ReleaseHistory />
        </div>
      </LoadingContent>
    </section>
  );
}
